import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { AnalysisToolbar, MetricCharts, usePlayerAnalysisState } from "../components/PlayerProfileModal";
import { TeamLogo, GameLabel } from "../components/TeamLogo";
import { AITrendPanel } from "../components/AITrendPanel";

export default function PlayerPage() {
  const { playerId } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [player, setPlayer] = useState(null);
  const [error, setError] = useState("");
  const st = usePlayerAnalysisState();

  useEffect(() => {
    setPlayer(null);
    setError("");
    api.get(`/players/${playerId}`)
      .then((r) => setPlayer(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load this player"));
  }, [playerId]);

  return (
    <div data-testid="player-page" className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 space-y-4">
      <button
        data-testid="player-page-back"
        onClick={() =>
          player?.matchup_id
            ? nav(`/?sport=${player.sport}&source=${player.source || "live"}&open=${player.matchup_id}`)
            : nav("/")
        }
        className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {error && <p data-testid="player-page-error" className="text-sm text-[#EF4444]">{error}</p>}
      {!player && !error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
        </div>
      )}

      {player && (
        <>
          <div className="flex items-center gap-4 rounded-2xl border border-[#233248] bg-[#141C2B] p-4">
            <TeamLogo sport={player.sport} abbr={player.team_abbr} color={player.color} size="2xl" />
            <div className="min-w-0">
              <h1 data-testid="player-page-name" className="font-heading font-extrabold text-white text-2xl sm:text-3xl leading-tight">
                {player.name}
              </h1>
              <p className="font-data text-xs text-slate-400 flex items-center gap-1 flex-wrap">
                {player.position} ·{" "}
                {player.game_label ? <GameLabel sport={player.sport} label={player.game_label} size="sm" /> : null}
                {player.next_game && (
                  <span className="inline-flex items-center gap-1">
                    · next: {player.next_game.home ? "vs" : "@"}
                    <TeamLogo sport={player.sport} abbr={player.next_game.opp} logo={player.next_game.logo || undefined} size="sm" />
                    {new Date(player.next_game.date).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="sticky top-16 z-20 -mx-3 sm:mx-0 px-3 sm:px-4 py-3 rounded-none sm:rounded-xl border-y sm:border border-[#233248] bg-[#0B0F17]/90 backdrop-blur space-y-3">
            <AnalysisToolbar {...st} />
          </div>

          <AITrendPanel player={player} frame={st.frame} />

          <MetricCharts player={player} frame={st.frame} view={st.view} columns={2}
            toolbar={<AnalysisToolbar {...st} />} expandId={searchParams.get("expand")} />
        </>
      )}
    </div>
  );
}
